import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useAuth } from '../../hooks/useAuth';
import {
  RefreshCw,
  Download,
  TrendingUp,
  TrendingDown,
  Minus,
  PauseCircle,
  Sparkles,
  CheckCircle2,
  DollarSign,
  ChevronLeft,
  ChevronRight,
  X,
  Music,
  Plus
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { supabase, isConfigured } from '../../../backend/services/supabase';
import PageHeader from '../PageHeader';
import { AddReleaseModal } from './AddReleaseModal';


interface Release {
  id: string;
  title: string;
  artwork_url?: string;
  artist?: {
    id: string;
    name: string;
  };
  campaigns: Campaign[];
  metaCampaigns?: MetaCampaign[];
  totalBudget?: number;
  totalSpent?: number;
}

interface Campaign {
  id: string;
  name: string;
  type: 'conversion_ads' | 'radio' | 'playlisting';
  status: string;
  budget?: number;
  release_id?: string;
}

interface MetaCampaign {
  id: string;
  name: string;
  status: string;
  daily_budget?: number;
  lifetime_budget?: number;
  spend?: number;
}

interface BudgetData {
  releaseId: string;
  month: string;
  invoicedAmount: number;
  adSpent: number;
  lastSyncedAt?: string;
  // Performance data
  impressions?: number;
  clicks?: number;
  conversions?: number;
  revenue?: number;
  cpm?: number;
  cpc?: number;
  ctr?: number;
  roas?: number;
}

interface PerformanceMetrics {
  roi: number;
  performanceScore: number;
  efficiency: number;
  trend: 'up' | 'down' | 'stable';
  projectedSpend: number;
  daysRemaining: number;
  suggestedAction: 'increase' | 'decrease' | 'maintain' | 'pause' | 'optimize';
}

const SESSION_STORAGE_KEY = 'monthlyBudgetPending';

const MIN_BUDGET_VALUE = 0;
const MAX_BUDGET_VALUE = 1_000_000;

const MONTH_RANGE_YEARS = 3;

interface MonthEntry {
  label: string;
  shortLabel: string;
  year: number;
  month: number;
  iso: string;
}

const generateMonthEntries = (year: number): MonthEntry[] => {
  const entries: MonthEntry[] = [];
  const startYear = year - MONTH_RANGE_YEARS;
  const endYear = year + MONTH_RANGE_YEARS;

  // Month names to avoid timezone issues
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  const shortMonthNames = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
  ];

  for (let y = startYear; y <= endYear; y++) {
    for (let m = 0; m < 12; m++) {
      entries.push({
        label: monthNames[m],
        shortLabel: shortMonthNames[m],
        year: y, // Explicitly use the loop year to avoid any date calculation issues
        month: m,
        iso: `${y}-${String(m + 1).padStart(2, '0')}`
      });
    }
  }

  return entries;
};

const CAMPAIGN_TYPE_COLORS = {
  conversion_ads: 'bg-green-500',
  radio: 'bg-blue-500',
  playlisting: 'bg-purple-500'
};

const CAMPAIGN_TYPE_LABELS = {
  conversion_ads: 'Conversion Ads',
  radio: 'Radio',
  playlisting: 'Playlisting'
};

const SUGGESTED_ACTION_ICONS: Record<PerformanceMetrics['suggestedAction'], { icon: LucideIcon; tone: string; label: string }> = {
  increase: { icon: TrendingUp, tone: 'text-green-600 dark:text-green-400', label: 'Increase budget' },
  decrease: { icon: TrendingDown, tone: 'text-red-600 dark:text-red-400', label: 'Decrease budget' },
  maintain: { icon: Minus, tone: 'text-blue-600 dark:text-blue-400', label: 'Maintain budget' },
  pause: { icon: PauseCircle, tone: 'text-gray-600 dark:text-gray-400', label: 'Pause spend' },
  optimize: { icon: Sparkles, tone: 'text-yellow-600 dark:text-yellow-400', label: 'Optimize budget' }
};

const buildKey = (releaseId: string, month: string) => `${releaseId}::${month}`;
const parseKey = (key: string) => {
  const [releaseId, month] = key.split('::');
  return { releaseId, month };
};

const PRIMARY_BUTTON_CLASSES = 'inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed dark:bg-blue-500 dark:hover:bg-blue-600 dark:focus:ring-offset-gray-900';
const SECONDARY_BUTTON_CLASSES = 'inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors disabled:opacity-60 disabled:cursor-not-allowed dark:text-gray-200 dark:bg-gray-800 dark:border-gray-600 dark:hover:bg-gray-700 dark:focus:ring-offset-gray-900';

export const MonthlyBudgets: React.FC = () => {
  const { user } = useAuth();
  const today = new Date();
  const currentYear = today.getFullYear();
  const currentMonthIndex = today.getMonth(); // 0-indexed: September = 8
  const [releases, setReleases] = useState<Release[]>([]);
  const [showAddReleaseModal, setShowAddReleaseModal] = useState(false);
  const [budgetData, setBudgetData] = useState<Map<string, BudgetData>>(new Map());
  const [selectedCell, setSelectedCell] = useState<{ releaseId: string; month: string } | null>(null);
  const [editingValue, setEditingValue] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [, setUserRole] = useState<string | null>(null);
  const [showPerformanceRows, setShowPerformanceRows] = useState(false);
  const [showForecastRows, setShowForecastRows] = useState(false);
  const [showInactiveCampaigns, setShowInactiveCampaigns] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const [pendingChanges, setPendingChanges] = useState<Map<string, number>>(new Map());
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [originalBudgetData, setOriginalBudgetData] = useState<Map<string, BudgetData>>(new Map());
  const [inputError, setInputError] = useState<string | null>(null);
  const [editingCell, setEditingCell] = useState<{ releaseId: string; month: string } | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const headerRefs = useRef<(HTMLTableCellElement | null)[]>([]);
  const monthEntries = useMemo(() => generateMonthEntries(currentYear), [currentYear]);
  const currentMonthEntryIndex = useMemo(
    () => monthEntries.findIndex(entry => entry.year === currentYear && entry.month === currentMonthIndex),
    [monthEntries, currentYear, currentMonthIndex]
  );
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [exportStartMonth, setExportStartMonth] = useState<string>('');
  const [exportEndMonth, setExportEndMonth] = useState<string>('');
  const [exportCampaignIds, setExportCampaignIds] = useState<string[]>([]);
  const [highlightMonthIndex, setHighlightMonthIndex] = useState(() => (currentMonthEntryIndex >= 0 ? currentMonthEntryIndex : 0));

  const canEdit = true;

  const highlightRef = useRef(highlightMonthIndex);

  useEffect(() => {
    highlightRef.current = highlightMonthIndex;
  }, [highlightMonthIndex]);

  const scrollToMonthIndex = useCallback((index: number, behavior: 'auto' | 'smooth' = 'auto') => {
    const container = scrollRef.current;
    if (!container || monthEntries.length === 0) return;

    const clamped = Math.max(0, Math.min(monthEntries.length - 1, index));
    setHighlightMonthIndex(clamped);
    const targetIndex = Math.max(0, Math.min(monthEntries.length - 1, clamped - 2));
    const targetCell = headerRefs.current[targetIndex] ?? headerRefs.current[clamped];
    if (targetCell) {
      const left = Math.max(0, targetCell.offsetLeft - 16);
      container.scrollTo({ left, behavior });
      return;
    }

    const fallbackWidth = headerRefs.current.find(cell => cell)?.offsetWidth ?? 120;
    const fallbackLeft = Math.max(0, targetIndex * fallbackWidth);
    container.scrollTo({ left: fallbackLeft, behavior });
  }, [monthEntries.length]);

  const updateVisibleMonthIndex = useCallback(() => {
    const container = scrollRef.current;
    if (!container || monthEntries.length === 0) return;

    const containerCenter = container.scrollLeft + container.clientWidth / 2;
    let closestIndex = highlightRef.current;
    let smallestDistance = Number.POSITIVE_INFINITY;
    const cells = headerRefs.current;
    for (let i = 0; i < cells.length; i++) {
      const cell = cells[i];
      if (!cell) continue;
      const cellCenter = cell.offsetLeft + cell.offsetWidth / 2;
      const distance = Math.abs(cellCenter - containerCenter);
      if (distance < smallestDistance) {
        smallestDistance = distance;
        closestIndex = i;
      }
    }
    setHighlightMonthIndex(closestIndex);
  }, [monthEntries.length]);

  const snapByMonths = useCallback((delta: number) => {
    if (monthEntries.length === 0) return;
    const target = Math.max(0, Math.min(monthEntries.length - 1, highlightMonthIndex + delta));
    scrollToMonthIndex(target, 'smooth');
  }, [monthEntries.length, highlightMonthIndex, scrollToMonthIndex]);

  const setDefaultExportSelection = useCallback(() => {
    if (monthEntries.length === 0) return;
    const startOfYear = monthEntries.find(entry => entry.year === currentYear && entry.month === 0) ?? monthEntries[0];
    const endOfYear = monthEntries.find(entry => entry.year === currentYear && entry.month === 11) ?? monthEntries[monthEntries.length - 1];
    setExportStartMonth(startOfYear.iso);
    setExportEndMonth(endOfYear.iso);
    const activeCampaigns = releases.filter(release => true).map(release => release.id);
    if (activeCampaigns.length > 0) {
      setExportCampaignIds(activeCampaigns);
    } else {
      setExportCampaignIds(releases.map(release => release.id));
    }
  }, [monthEntries, currentYear, releases]);

  const showValidationError = (message: string, cellKey?: string) => {
    setInputError(message);
    if (cellKey) {
      // Store error for specific cell
      setInputError(message);
    }
    toast.error(message);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const validateBudgetValue = (value: string): string | null => {
    if (value.trim() === '') return 'Budget amount is required.';
    const parsed = Number.parseFloat(value);
    if (!Number.isFinite(parsed)) return 'Enter a valid budget amount.';
    if (parsed < MIN_BUDGET_VALUE) return 'Budget amount cannot be negative.';
    if (parsed > MAX_BUDGET_VALUE) {
      return `Budget amount cannot exceed $${MAX_BUDGET_VALUE.toLocaleString()}.`;
    }
    const decimalPart = value.includes('.') ? value.split('.')[1] ?? '' : '';
    if (decimalPart.length > 2) {
      return 'Use no more than two decimal places.';
    }
    return null;
  };

  const formatBudgetInput = (value: number) => Number.isFinite(value) ? value.toFixed(2) : '0.00';

  const handleBudgetInputChange = (value: string) => {
    setEditingValue(value);
    if (value === '') {
      setInputError('Budget amount is required.');
      return;
    }
    const validation = validateBudgetValue(value);
    setInputError(validation);
  };





  const getMonthCellMeta = useCallback((entry: MonthEntry, index: number) => {
    const isCurrentMonth = index === highlightMonthIndex;
    const isQuarterBoundary = (entry.month + 1) % 3 === 0;
    const isYearBoundary = entry.month === 11;
    const borderClass = isYearBoundary
      ? 'border-r-2 border-gray-200 dark:border-gray-600'
      : isQuarterBoundary
        ? 'border-r border-gray-200 dark:border-gray-700'
        : 'border-r border-gray-100/70 dark:border-gray-800/50';
    return { isCurrentMonth, borderClass };
  }, [highlightMonthIndex]);
  // demo mode: either ?demo=true in the URL or running in development
  const isDemo = typeof window !== 'undefined'
    ? (new URLSearchParams(window.location.search).get('demo') === 'true' || !isConfigured)
    : !isConfigured;

  const persistPendingChanges = useCallback((changes: Map<string, number>) => {
    if (typeof window === 'undefined') return;
    if (changes.size === 0) {
      window.sessionStorage.removeItem(SESSION_STORAGE_KEY);
    } else {
      const serialized: Record<string, number> = {};
      changes.forEach((value, key) => {
        serialized[key] = value;
      });
      window.sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(serialized));
    }
  }, []);

  const updatePendingChanges = useCallback((updater: (prev: Map<string, number>) => Map<string, number>) => {
    setPendingChanges(prev => {
      const updated = updater(prev);
      const hasChanges = updated.size > 0;
      setHasUnsavedChanges(hasChanges);
      persistPendingChanges(updated);
      return updated;
    });
  }, [persistPendingChanges]);

  // Load releases and budget data (allow loading without auth in demo/seeded env)
  useEffect(() => {
    if (user) {
      fetchUserRole();
    } else if (isDemo) {
      // give demo full-edit permissions
      setUserRole('admin');
    }
    // Always attempt to load data so seeded Supabase shows on Vercel without login
    loadData();
  }, [user, showInactiveCampaigns]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        event.preventDefault();
        event.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    const frame = window.requestAnimationFrame(() => {
      // Find the current month (September 2025) and scroll to it
      const targetIndex = monthEntries.findIndex(entry => 
        entry.year === currentYear && entry.month === currentMonthIndex
      );
      const scrollIndex = targetIndex >= 0 ? targetIndex : 0;
      scrollToMonthIndex(scrollIndex, 'auto');
      updateVisibleMonthIndex();
    });

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.cancelAnimationFrame(frame);
    };
  }, [hasUnsavedChanges, currentMonthEntryIndex, scrollToMonthIndex, updateVisibleMonthIndex]);

  useEffect(() => {
    if (isLoading) return;
    // Find the current month (September 2025) and scroll to it after loading
    const targetIndex = monthEntries.findIndex(entry => 
      entry.year === currentYear && entry.month === currentMonthIndex
    );
    const scrollIndex = targetIndex >= 0 ? targetIndex : 0;
    const frame = window.requestAnimationFrame(() => scrollToMonthIndex(scrollIndex, 'auto'));
    return () => window.cancelAnimationFrame(frame);
  }, [isLoading, monthEntries, currentYear, currentMonthIndex, scrollToMonthIndex]);

  useEffect(() => {
    // Find and highlight the current month (September 2025)
    const targetIndex = monthEntries.findIndex(entry => 
      entry.year === currentYear && entry.month === currentMonthIndex
    );
    if (targetIndex >= 0) {
      setHighlightMonthIndex(targetIndex);
    } else {
      // Fallback to first month if current month not found
      setHighlightMonthIndex(0);
    }
  }, [monthEntries, currentYear, currentMonthIndex]);

  useEffect(() => {
    headerRefs.current = Array(monthEntries.length).fill(null);
  }, [monthEntries.length]);


  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    const handle = () => updateVisibleMonthIndex();

    container.addEventListener('scroll', handle, { passive: true });
    window.addEventListener('resize', handle);
    updateVisibleMonthIndex();

    return () => {
      container.removeEventListener('scroll', handle);
      window.removeEventListener('resize', handle);
    };
  }, [updateVisibleMonthIndex, monthEntries.length]);



  // Fetch the user's role
  const fetchUserRole = async () => {
    if (!user?.id) return;

    try {
      const { data, error } = await supabase
        .from('user_roles')
        .select('role:roles(name)')
        .eq('user_id', user.id);

      if (error) throw error;

      if (data && data.length > 0) {
        const roleNames = data
          .map((entry: any) => entry.role?.name)
          .filter(Boolean) as string[];

        if (roleNames.includes('admin')) {
          setUserRole('admin');
        } else if (roleNames.includes('manager')) {
          setUserRole('manager');
        } else if (roleNames.length > 0) {
          setUserRole(roleNames[0]);
        } else {
          setUserRole('manager');
        }
      } else {
        // If no explicit role assignments, allow editing by default
        setUserRole('manager');
      }
    } catch (error) {
      console.error('Error fetching user role:', error);
      // If role lookups fail, assume admin in demo environments so UI remains usable
      const code = (error as any)?.code;
      const msg = (error as any)?.message || '';
      if (code === 'PGRST205' || /public\.profiles/.test(msg)) {
        setUserRole('admin');
      } else {
        setUserRole('manager');
      }
    }
  };

  const loadData = async () => {
    try {
      setIsLoading(true);

      // Load from Supabase (now includes seed data for demo mode)
      
      // Load releases with their campaigns
      const storedReleaseIds = JSON.parse(localStorage.getItem('budgetManagerReleases') || '[]');
      
      let releasesData = [];
      if (storedReleaseIds.length > 0) {
        // Load specific releases
        const { data, error } = await supabase
          .from('releases')
          .select(`
            id,
            title,
            artwork_url,
            artist:artists(id, name),
            campaigns(
              id,
              name,
              budget,
              status
            ),
            metaCampaigns:meta_campaigns!meta_campaigns_release_id_fkey(
              id,
              name,
              status,
              campaign_id
            )
          `)
          .in('id', storedReleaseIds)
          .order('created_at', { ascending: false });
        
        if (error) throw error;
        releasesData = data || [];
      } else {
        // Load all releases (including those with meta_campaigns only)
        const { data, error } = await supabase
          .from('releases')
          .select(`
            id,
            title,
            artwork_url,
            artist:artists(id, name),
            campaigns(
              id,
              name,
              budget,
              status
            ),
            metaCampaigns:meta_campaigns!meta_campaigns_release_id_fkey(
              id,
              name,
              status,
              campaign_id
            )
          `)
          .order('created_at', { ascending: false });
        
        if (error) throw error;
        releasesData = data || [];
      }

      // DEBUG: Log what we got
      console.log('Loaded releases:', releasesData?.length);
      console.log('Sample release:', releasesData?.[0]);
      console.log('Meta campaigns on first release:', releasesData?.[0]?.metaCampaigns);
      
      // Filter to only show releases with active or paused campaigns
      const filteredReleases = (releasesData || []).filter((release: any) => {
        const hasCampaigns = (release.campaigns && release.campaigns.length > 0) || 
                           (release.metaCampaigns && release.metaCampaigns.length > 0);
        
        console.log(`Release "${release.title}":`, {
          campaigns: release.campaigns?.length || 0,
          metaCampaigns: release.metaCampaigns?.length || 0,
          metaStatuses: release.metaCampaigns?.map((c: any) => c.status)
        });
        
        if (!hasCampaigns) return false;

        // Check if any campaigns are active or paused
        const hasActiveCampaigns = release.campaigns?.some((c: any) => 
          c.status === 'active' || c.status === 'paused'
        );
        const hasActiveMetaCampaigns = release.metaCampaigns?.some((c: any) => 
          c.status?.toUpperCase() === 'ACTIVE' || c.status?.toUpperCase() === 'PAUSED'
        );

        // If showing inactive, include all releases with campaigns
        if (showInactiveCampaigns) {
          return hasCampaigns;
        }

        // Otherwise, only show releases with active/paused campaigns
        return hasActiveCampaigns || hasActiveMetaCampaigns;
      });

      console.log('Filtered releases:', filteredReleases.length);
      setReleases(filteredReleases);

      // Load budget data for current year
      const startDate = new Date(Date.UTC(currentYear - MONTH_RANGE_YEARS, 0, 1, 0, 0, 0));
      const endDate = new Date(Date.UTC(currentYear + MONTH_RANGE_YEARS, 11, 31, 23, 59, 59));
      
      const { data: budgetsData, error: budgetsError } = await supabase
        .from('campaign_budgets')
        .select(`
          *,
          campaign:campaigns(
            id,
            release_id
          )
        `)
        .gte('month', startDate.toISOString())
        .lte('month', endDate.toISOString());

      if (budgetsError) throw budgetsError;

      // Load performance data from ad spend events
      // Note: ad_spend_events doesn't have FK constraint to campaigns, so we query separately
      let performanceData: any[] = [];
      try {
        const { data, error: performanceError } = await supabase
          .from('ad_spend_events')
          .select('campaign_id, amount, raw, event_hour')
          .gte('event_hour', startDate.toISOString())
          .lte('event_hour', endDate.toISOString());

        if (performanceError) {
          console.warn('Could not load performance data:', performanceError);
        } else {
          performanceData = data || [];
        }
      } catch (error) {
        console.warn('Performance data query failed:', error);
      }

      // Create a mapping from campaign_id to release_id for performance data filtering
      const campaignToReleaseMap = new Map<string, string>();
      budgetsData?.forEach((budget: any) => {
        if (budget.campaign_id && budget.campaign?.release_id) {
          campaignToReleaseMap.set(budget.campaign_id, budget.campaign.release_id);
        }
      });

      // Convert to map for easier access with enhanced performance metrics
      const budgetMap = new Map<string, BudgetData>();
      let latestBudgetSync: number | null = null;

      budgetsData?.forEach((budget: {
        campaign_id: string;
        month: string;
        invoiced_amount?: number;
        ad_spent?: number;
        last_synced_at?: string;
        campaign?: {
          id: string;
          release_id: string;
        };
      }) => {
        const normalizedMonth = budget.month?.slice(0, 7);
        if (!normalizedMonth || !budget.campaign?.release_id) return;
        const key = buildKey(budget.campaign.release_id, normalizedMonth);
        
        // Aggregate performance data for this release/month
        const monthStart = new Date(`${normalizedMonth}-01T00:00:00Z`);
        const monthEnd = new Date(Date.UTC(
          monthStart.getUTCFullYear(),
          monthStart.getUTCMonth() + 1,
          0,
          23,
          59,
          59
        ));
        
        const monthPerformance = (performanceData || []).filter((p: any) => {
          const eventDate = new Date(p.event_hour);
          const performanceReleaseId = campaignToReleaseMap.get(p.campaign_id);
          return performanceReleaseId === budget.campaign.release_id && 
                 eventDate >= monthStart && 
                 eventDate <= monthEnd;
        });

        let impressions = 0, clicks = 0, conversions = 0;
        let totalCpm = 0, totalCpc = 0, totalCtr = 0, cpmCount = 0, cpcCount = 0, ctrCount = 0;

        monthPerformance.forEach((p: any) => {
          if (p.raw) {
            impressions += p.raw.impressions || 0;
            clicks += p.raw.clicks || 0;
            conversions += p.raw.conversions || 0;
            if (p.raw.cpm) { totalCpm += parseFloat(p.raw.cpm); cpmCount++; }
            if (p.raw.cpc) { totalCpc += parseFloat(p.raw.cpc); cpcCount++; }
            if (p.raw.ctr) { totalCtr += parseFloat(p.raw.ctr); ctrCount++; }
          }
        });

        // Aggregate campaign budgets by release/month
        const existingData = budgetMap.get(key);
        const currentInvoiced = budget.invoiced_amount || 0;
        const currentSpent = budget.ad_spent || 0;
        
        if (existingData) {
          // Add to existing aggregation
          budgetMap.set(key, {
            ...existingData,
            invoicedAmount: existingData.invoicedAmount + currentInvoiced,
            adSpent: existingData.adSpent + currentSpent,
            lastSyncedAt: budget.last_synced_at || existingData.lastSyncedAt,
            // Aggregate performance metrics
            impressions: existingData.impressions + impressions,
            clicks: existingData.clicks + clicks,
            conversions: existingData.conversions + conversions,
            revenue: (existingData.conversions + conversions) * 25,
            roas: (existingData.adSpent + currentSpent) ? ((existingData.conversions + conversions) * 25) / (existingData.adSpent + currentSpent) : 0
          });
        } else {
          // Create new entry
          budgetMap.set(key, {
            releaseId: budget.campaign.release_id,
            month: normalizedMonth,
            invoicedAmount: currentInvoiced,
            adSpent: currentSpent,
            lastSyncedAt: budget.last_synced_at,
            // Performance metrics
            impressions,
            clicks,
            conversions,
            revenue: conversions * 25,
            cpm: cpmCount > 0 ? totalCpm / cpmCount : 0,
            cpc: cpcCount > 0 ? totalCpc / cpcCount : 0,
            ctr: ctrCount > 0 ? totalCtr / ctrCount : 0,
            roas: currentSpent ? (conversions * 25) / currentSpent : 0
          });
        }

        if (budget.last_synced_at) {
          const syncTime = new Date(budget.last_synced_at).getTime();
          if (!isNaN(syncTime)) {
            latestBudgetSync = Math.max(latestBudgetSync ?? syncTime, syncTime);
          }
        }
      });
      setBudgetData(budgetMap);
      setOriginalBudgetData(new Map(budgetMap));

      if (typeof window !== 'undefined') {
        try {
          const stored = window.sessionStorage.getItem(SESSION_STORAGE_KEY);
          if (stored) {
            const parsed: Record<string, number> = JSON.parse(stored);
            const restoredChanges = new Map<string, number>();
            const restoredBudget = new Map(budgetMap);
            Object.entries(parsed).forEach(([key, value]) => {
              restoredChanges.set(key, value);
              const existing = restoredBudget.get(key);
              if (existing) {
                restoredBudget.set(key, { ...existing, invoicedAmount: value });
              }
            });
            setPendingChanges(restoredChanges);
            setBudgetData(restoredBudget);
            const hasChanges = restoredChanges.size > 0;
            setHasUnsavedChanges(hasChanges);
            if (!hasChanges) {
              persistPendingChanges(restoredChanges);
            }
          } else {
            setPendingChanges(new Map());
            setHasUnsavedChanges(false);
            persistPendingChanges(new Map());
          }
        } catch (storageError) {
          console.warn('Failed to restore pending edits:', storageError);
          setPendingChanges(new Map());
          setHasUnsavedChanges(false);
          persistPendingChanges(new Map());
        }
      }

    } catch (error: any) {
      console.error('Error loading data:', error);
      console.error('Error details:', error?.message, error?.details, error?.hint);
      toast.error(`Failed to load budget data: ${error?.message || 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Sync Meta Ads data
  const syncMetaAds = async () => {
    try {
      setIsSyncing(true);
      
      // Call your Meta Ads sync endpoint
      const session = await supabase.auth.getSession();
      const accessToken = session.data.session?.access_token;
      if (!accessToken) {
        toast.error('You need to be signed in to sync Meta Ads data.');
        return;
      }

      // Get all meta campaign IDs from releases with linked campaigns
      const metaCampaignIds = releases
        .flatMap(release => release.metaCampaigns || [])
        .filter(mc => mc.id)
        .map(mc => mc.id);

      if (metaCampaignIds.length === 0) {
        toast.info('No Meta campaigns found. Please link campaigns to releases in Meta Alignment page.');
        return;
      }

      const now = new Date();
      const sinceISO = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1, 0, 0, 0)).toISOString();
      const untilISO = now.toISOString();
      
      console.log('Syncing Meta campaigns:', metaCampaignIds);
      
      const response = await fetch('/api/sync-meta-ads', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify({
          campaignIds: metaCampaignIds,
          since: sinceISO,
          until: untilISO
        })
      });
      if (!response.ok) {
        let detail: any = null;
        try { detail = await response.json(); } catch { /* ignore */ }
        console.error('Sync Meta Ads failed:', response.status, detail || await response.text());
        throw new Error(detail?.error || 'Sync failed');
      }
      
      toast.success('Meta Ads data synced successfully');
      await loadData(); // Reload data
      
    } catch (error) {
      console.error('Error syncing Meta Ads:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to sync Meta Ads data');
    } finally {
      setIsSyncing(false);
    }
  };

  // Handle cell navigation
  const handleKeyDown = (e: React.KeyboardEvent, releaseId: string, monthIndex: number) => {
    if (!canEdit) return;

    const key = e.key.toLowerCase();
    
    // Bulk editing shortcuts
    if ((e.ctrlKey || e.metaKey) && key === 'a') {
      e.preventDefault();
      // Select all cells in current row
      const release = releases.find(c => c.id === releaseId);
      if (release) {
        // Could implement row selection here
        toast.info('Row selection not yet implemented');
      }
      return;
    }
    
    if ((e.ctrlKey || e.metaKey) && key === 'c') {
      e.preventDefault();
      // Copy current cell value
      navigator.clipboard.writeText(editingValue);
      toast.success('Value copied to clipboard');
      return;
    }
    
    if ((e.ctrlKey || e.metaKey) && key === 'v') {
      e.preventDefault();
      // Paste from clipboard
      navigator.clipboard.readText().then(text => {
        const validation = validateBudgetValue(text);
        if (!validation) {
          setEditingValue(text);
          handleBudgetInputChange(text);
        } else {
          toast.error('Invalid value in clipboard');
        }
      }).catch(() => {
        toast.error('Failed to read clipboard');
      });
      return;
    }
    

    switch (e.key) {
      case 'ArrowUp':
        e.preventDefault();
        navigateToCell(releaseId, monthIndex, 'up');
        break;
      case 'ArrowDown':
        e.preventDefault();
        navigateToCell(releaseId, monthIndex, 'down');
        break;
      case 'ArrowLeft':
        e.preventDefault();
        navigateToCell(releaseId, monthIndex, 'left');
        break;
      case 'ArrowRight':
      case 'Tab':
        e.preventDefault();
        navigateToCell(releaseId, monthIndex, 'right');
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedCell) {
          saveCell();
        }
        break;
      case 'Escape':
        e.preventDefault();
        setSelectedCell(null);
        setEditingCell(null);
        setEditingValue('');
        setInputError(null);
        break;
    }
  };

  const navigateToCell = (currentCampaignId: string, currentMonthIndex: number, direction: 'up' | 'down' | 'left' | 'right') => {
    if (monthEntries.length === 0) return;
    const releaseIndex = releases.findIndex(c => c.id === currentCampaignId);
    let newCampaignIndex = releaseIndex;
    let newMonthIndex = currentMonthIndex;

    switch (direction) {
      case 'up':
        newCampaignIndex = Math.max(0, releaseIndex - 1);
        break;
      case 'down':
        newCampaignIndex = Math.min(releases.length - 1, releaseIndex + 1);
        break;
      case 'left':
        newMonthIndex = Math.max(0, currentMonthIndex - 1);
        break;
      case 'right':
        newMonthIndex = Math.min(monthEntries.length - 1, currentMonthIndex + 1);
        break;
    }

    const newRelease = releases[newCampaignIndex];
    if (newRelease) {
      selectCell(newRelease.id, newMonthIndex);
    }
  };

  const selectCell = (releaseId: string, monthIndex: number) => {
    if (selectedCell) {
      saveCell();
    }

    const entry = monthEntries[monthIndex];
    if (!entry) return;
    const key = buildKey(releaseId, entry.iso);
    const existingData = budgetData.get(key);

    setSelectedCell({ releaseId, month: entry.iso });
    setEditingCell({ releaseId, month: entry.iso });
    setEditingValue(formatBudgetInput(existingData?.invoicedAmount ?? 0));
    setInputError(null);

    scrollToMonthIndex(monthIndex, 'smooth');

    setTimeout(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    }, 0);
  };

  const saveCell = async () => {
    if (!selectedCell || !canEdit) return;

    const validationMessage = validateBudgetValue(editingValue);
    if (validationMessage) {
      showValidationError(validationMessage, buildKey(selectedCell.releaseId, selectedCell.month));
      return;
    }

    const parsedValue = Number.parseFloat(editingValue);
    const normalizedValue = Math.round(parsedValue * 100) / 100;
    const month = selectedCell.month;
    const key = buildKey(selectedCell.releaseId, month);
    const existingData = budgetData.get(key);
    const previousValue = existingData?.invoicedAmount ?? 0;

    if (Math.abs(previousValue - normalizedValue) < 0.0001) {
      setInputError(null);
      setEditingValue(formatBudgetInput(previousValue));
      setEditingCell(null);
      return;
    }



    setBudgetData(prev => {
      const newMap = new Map(prev);
      const current = newMap.get(key);
      if (current) {
        newMap.set(key, { ...current, invoicedAmount: normalizedValue });
      } else {
        newMap.set(key, {
          releaseId: selectedCell.releaseId,
          month,
          invoicedAmount: normalizedValue,
          adSpent: 0
        });
      }
      return newMap;
    });

    setEditingValue(formatBudgetInput(normalizedValue));
    setInputError(null);

    updatePendingChanges(prev => {
      const newMap = new Map(prev);
      const originalValue = originalBudgetData.get(key)?.invoicedAmount ?? 0;
      if (Math.abs(normalizedValue - originalValue) < 0.0001) {
        newMap.delete(key);
      } else {
        newMap.set(key, normalizedValue);
      }
      return newMap;
    });

    // Clear editing state
    setEditingCell(null);
  };


  const handleSaveAll = useCallback(async () => {
    if (inputError) {
      toast.error('Resolve validation errors before saving.');
      return;
    }
    if (pendingChanges.size === 0) return;

    try {
      setIsSaving(true);
      if (!isDemo) {
        // Build payload for campaign budgets
        const payload: any[] = [];
        
        for (const [key, value] of pendingChanges.entries()) {
          const { releaseId, month } = parseKey(key);
          const release = releases.find(r => r.id === releaseId);
          const activeCampaigns = release?.campaigns?.filter(c => c.status === 'active' || c.status === 'draft') || [];
          
          if (activeCampaigns.length === 0) {
            // If no active campaigns, skip this release
            continue;
          }
          
          // Distribute release budget equally across active campaigns
          const budgetPerCampaign = value / activeCampaigns.length;
          
          activeCampaigns.forEach(campaign => {
            payload.push({
              campaign_id: campaign.id,
              month: new Date(`${month}-01T00:00:00Z`).toISOString(),
              invoiced_amount: budgetPerCampaign,
              updated_at: new Date().toISOString()
            });
          });
        }

        if (payload.length > 0) {
          const { error } = await supabase
            .from('campaign_budgets')
            .upsert(payload, { onConflict: 'campaign_id,month' });

          if (error) throw error;
        }
      }

      const snapshot = new Map(budgetData);
      setOriginalBudgetData(snapshot);
      updatePendingChanges(() => new Map());
      const savedAt = new Date();
      setLastSavedAt(savedAt);
      setInputError(null);
      toast.success(isDemo ? 'Changes stored for this session.' : 'Budget changes saved.');
    } catch (error) {
      console.error('Error saving budgets:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to save budgets');
    } finally {
      setIsSaving(false);
    }
  }, [inputError, pendingChanges, isDemo, budgetData, updatePendingChanges]);

  const handleDiscardChanges = useCallback(() => {
    setBudgetData(new Map(originalBudgetData));
    updatePendingChanges(() => new Map());
    setSelectedCell(null);
    setEditingValue('');
    setInputError(null);
    toast.info('Pending budget changes discarded.');
  }, [originalBudgetData, updatePendingChanges]);

  const getBudgetStatus = (release: Campaign, monthIndex: number): 'normal' | 'warning' | 'critical' => {
    const entry = monthEntries[monthIndex];
    if (!entry) return 'normal';
    const key = buildKey(release.id, entry.iso);
    const data = budgetData.get(key);
    
    if (!data || data.invoicedAmount === 0) return 'normal';
    
    const spentPercentage = (data.adSpent / data.invoicedAmount) * 100;
    const threshold = release.budgetAlertThreshold || 25;
    
    if (spentPercentage >= (100 - threshold)) return 'critical';
    if (spentPercentage >= (100 - threshold * 2)) return 'warning';
    return 'normal';
  };

  const calculatePerformanceMetrics = (release: Campaign, monthIndex: number): PerformanceMetrics => {
    const entry = monthEntries[monthIndex];
    if (!entry) {
      return {
        roi: 0,
        performanceScore: 0,
        efficiency: 0,
        trend: 'stable',
        projectedSpend: 0,
        daysRemaining: 0,
        suggestedAction: 'maintain'
      };
    }
    const key = buildKey(release.id, entry.iso);
    const data = budgetData.get(key);
    
    if (!data || data.adSpent === 0) {
      return {
        roi: 0,
        performanceScore: 0,
        efficiency: 0,
        trend: 'stable',
        projectedSpend: data?.invoicedAmount || 0,
        daysRemaining: 30,
        suggestedAction: 'maintain'
      };
    }

    const roi = ((data.revenue || 0) - data.adSpent) / data.adSpent;
    const spendEfficiency = data.clicks ? data.adSpent / data.clicks : 0;
    const ctrScore = Math.min((data.ctr || 0) / 2.0, 1) * 100; // 2% CTR = 100% score
    const roasScore = Math.min((data.roas || 0) / 3.0, 1) * 100; // 3.0 ROAS = 100% score
    const performanceScore = (ctrScore + roasScore) / 2;
    
    // Calculate projection based on current pace
    const currentDate = new Date();
    const monthEnd = new Date(entry.year, entry.month + 1, 0);
    const daysInMonth = monthEnd.getDate();
    const isCurrentMonthEntry = entry.year === currentDate.getFullYear() && entry.month === currentDate.getMonth();
    const daysElapsed = isCurrentMonthEntry ? Math.max(1, currentDate.getDate()) : daysInMonth;
    const dailySpend = data.adSpent / daysElapsed;
    const projectedSpend = dailySpend * daysInMonth;
    const remainingBudget = Math.max(0, (data.invoicedAmount || 0) - data.adSpent);
    const daysRemaining = dailySpend > 0 ? Math.floor(remainingBudget / dailySpend) : 0;

    // Determine suggested action
    let suggestedAction: PerformanceMetrics['suggestedAction'] = 'maintain';
    if (roi > 1.5 && performanceScore > 70) suggestedAction = 'increase';
    else if (roi < 0.5 || performanceScore < 30) suggestedAction = 'pause';
    else if (performanceScore < 50) suggestedAction = 'optimize';
    else if (projectedSpend > data.invoicedAmount * 1.1) suggestedAction = 'decrease';

    return {
      roi,
      performanceScore,
      efficiency: spendEfficiency,
      trend: roi > 1 ? 'up' : roi < 0.5 ? 'down' : 'stable',
      projectedSpend,
      daysRemaining,
      suggestedAction
    };
  };

  const getPerformanceColor = (value: number, type: 'roi' | 'score' | 'trend') => {
    switch (type) {
      case 'roi':
        if (value > 1.5) return 'text-green-600 dark:text-green-400';
        if (value > 0.8) return 'text-yellow-600 dark:text-yellow-400';
        return 'text-red-600 dark:text-red-400';
      case 'score':
        if (value > 70) return 'text-green-600 dark:text-green-400';
        if (value > 40) return 'text-yellow-600 dark:text-yellow-400';
        return 'text-red-600 dark:text-red-400';
      case 'trend':
        return value > 1 ? 'text-green-600 dark:text-green-400' : 
               value < 0.5 ? 'text-red-600 dark:text-red-400' : 
               'text-gray-600 dark:text-gray-400';
      default:
        return 'text-gray-600 dark:text-gray-400';
    }
  };

  const exportToCSV = (startIso: string, endIso: string, selectedCampaignIds: string[]): boolean => {
    if (monthEntries.length === 0) {
      toast.error('No months available for export.');
      return false;
    }

    if (!startIso || !endIso) {
      toast.error('Select a start and end month.');
      return false;
    }

    const startIndex = monthEntries.findIndex(entry => entry.iso === startIso);
    const endIndex = monthEntries.findIndex(entry => entry.iso === endIso);

    if (startIndex === -1 || endIndex === -1) {
      toast.error('Selected months are not in range.');
      return false;
    }

    if (selectedCampaignIds.length === 0) {
      toast.error('Select at least one release to export.');
      return false;
    }

    const start = Math.min(startIndex, endIndex);
    const end = Math.max(startIndex, endIndex);
    const range = monthEntries.slice(start, end + 1);

    const headers = [
      'Campaign',
      'Type',
      'Artist',
      'Song',
      'Client',
      ...range.map(entry => `${entry.shortLabel} ${entry.year}`),
      'Total'
    ];
    const rows = releases
      .filter(release => selectedCampaignIds.includes(release.id))
      .map(release => {
        const row = [
          release.title,
          `${release.campaigns?.length || 0} campaigns`,
          release.artist?.name || '',
          release.title || '',
        release.client?.name || ''
      ];
      
      let total = 0;
      range.forEach((entry) => {
        const key = buildKey(release.id, entry.iso);
        const data = budgetData.get(key);
        const amount = data?.invoicedAmount || 0;
        total += amount;
        row.push(amount.toString());
      });
      
      row.push(total.toString());
      return row;
      });

    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const startLabel = range[0].iso;
    const endLabel = range[range.length - 1].iso;
    a.download = `budget-${startLabel}-to-${endLabel}.csv`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 0);
    return true;
  };


  if (isLoading) {
    return <div className="flex justify-center items-center h-64">Loading...</div>;
  }

  return (
    <div className="p-6 bg-gray-50 dark:bg-gray-900 min-h-full">
      <div className="max-w-7xl mx-auto space-y-6">
        <PageHeader
          icon={<DollarSign className="h-8 w-8 text-blue-600 dark:text-blue-400" />}
          title="Monthly Budget"
          subtitle="Track and manage budgets for your releases by month"
          actions={
            <div className="flex items-center flex-wrap gap-3">
              <button
                onClick={() => setShowAddReleaseModal(true)}
                className={PRIMARY_BUTTON_CLASSES}
              >
                <Plus className="h-4 w-4" />
                Add Releases
              </button>
              <button
                onClick={handleSaveAll}
                className={(hasUnsavedChanges || isSaving) ? PRIMARY_BUTTON_CLASSES : SECONDARY_BUTTON_CLASSES}
                disabled={isSaving || pendingChanges.size === 0}
              >
                <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
                {isSaving ? 'Saving…' : hasUnsavedChanges ? 'Save changes' : 'Saved'}
              </button>
              <button
                onClick={syncMetaAds}
                disabled={isSyncing}
                className={SECONDARY_BUTTON_CLASSES}
              >
                <RefreshCw className={`h-4 w-4 ${isSyncing ? 'animate-spin' : ''}`} aria-hidden="true" />
                Sync
              </button>
              <button
                onClick={() => {
                  setDefaultExportSelection();
                  setIsExportModalOpen(true);
                }}
                className={SECONDARY_BUTTON_CLASSES}
              >
                <Download className="h-4 w-4" aria-hidden="true" />
                Export
              </button>
            </div>
          }
        />

        {/* Toggle Buttons + Month Nav */}
        <div className="flex flex-wrap gap-3 mb-4 items-center justify-between">
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => setShowPerformanceRows(!showPerformanceRows)}
              className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ease-out backdrop-blur-sm shadow-sm hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400/40 motion-reduce:transition-none ${
                showPerformanceRows 
                  ? 'bg-emerald-50/80 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-200 border border-emerald-200/60 dark:border-emerald-800/60 shadow-emerald-200/50 dark:shadow-emerald-900/50'
                  : 'bg-white/70 dark:bg-gray-800/50 text-gray-600 dark:text-gray-200 border border-gray-200/60 dark:border-gray-700/60 hover:bg-gray-50/80 dark:hover:bg-gray-700/60'
              }`}
            >
              <TrendingUp className="h-4 w-4" />
              Performance Data
            </button>
            <button
              onClick={() => setShowForecastRows(!showForecastRows)}
              className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ease-out backdrop-blur-sm shadow-sm hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400/40 motion-reduce:transition-none ${
                showForecastRows 
                  ? 'bg-amber-50/80 dark:bg-amber-950/40 text-amber-700 dark:text-amber-200 border border-amber-200/60 dark:border-amber-800/60 shadow-amber-200/50 dark:shadow-amber-900/50'
                  : 'bg-white/70 dark:bg-gray-800/50 text-gray-600 dark:text-gray-200 border border-gray-200/60 dark:border-gray-700/60 hover:bg-gray-50/80 dark:hover:bg-gray-700/60'
              }`}
            >
              <Sparkles className="h-4 w-4" />
              Forecasts
            </button>
            <button
              onClick={() => setShowInactiveCampaigns(!showInactiveCampaigns)}
              className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ease-out backdrop-blur-sm shadow-sm hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400/40 motion-reduce:transition-none ${
                showInactiveCampaigns 
                  ? 'bg-gray-50/80 dark:bg-gray-950/40 text-gray-700 dark:text-gray-200 border border-gray-200/60 dark:border-gray-800/60'
                  : 'bg-white/70 dark:bg-gray-800/50 text-gray-600 dark:text-gray-200 border border-gray-200/60 dark:border-gray-700/60 hover:bg-gray-50/80 dark:hover:bg-gray-700/60'
              }`}
            >
              <PauseCircle className="h-4 w-4" />
              {showInactiveCampaigns ? 'Hide Inactive' : 'Show Inactive'}
            </button>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => snapByMonths(-1)}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium bg-white/80 dark:bg-gray-800/60 text-gray-700 dark:text-gray-200 border border-gray-200/60 dark:border-gray-700/60 hover:bg-gray-50/80 dark:hover:bg-gray-700/80 disabled:opacity-40 disabled:cursor-not-allowed backdrop-blur-sm transition-all duration-200 ease-out shadow-sm hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400/40 motion-reduce:transition-none"
              title="Previous month"
              disabled={highlightMonthIndex <= 0}
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button
              onClick={() => snapByMonths(1)}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium bg-white/80 dark:bg-gray-800/60 text-gray-700 dark:text-gray-200 border border-gray-200/60 dark:border-gray-700/60 hover:bg-gray-50/80 dark:hover:bg-gray-700/80 disabled:opacity-40 disabled:cursor-not-allowed backdrop-blur-sm transition-all duration-200 ease-out shadow-sm hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400/40 motion-reduce:transition-none"
              title="Next month"
              disabled={highlightMonthIndex >= monthEntries.length - 1}
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div className="bg-white/80 dark:bg-gray-800/60 backdrop-blur-xl rounded-2xl shadow-2xl border border-gray-200/50 dark:border-gray-700/50 overflow-visible">
          {inputError && (
            <div id="budget-input-error" className="mx-6 mt-4 mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-800/60 dark:bg-red-900/30 dark:text-red-200">
              {inputError}
            </div>
          )}

          {hasUnsavedChanges && (
            <div className="px-6 py-3 bg-amber-50 dark:bg-amber-900/20 border-b border-amber-200 dark:border-amber-700 flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-amber-900 dark:text-amber-200">You have unsaved budget changes.</p>
                <p className="text-xs text-amber-800 dark:text-amber-300">
                  {lastSavedAt ? `Last saved ${lastSavedAt.toLocaleString()}` : 'No saved changes yet'}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={handleDiscardChanges}
                  className={SECONDARY_BUTTON_CLASSES}
                  disabled={isSaving}
                >
                  Discard
                </button>
                <button
                  onClick={handleSaveAll}
                  className={PRIMARY_BUTTON_CLASSES}
                  disabled={isSaving}
                >
                  {isSaving ? 'Saving...' : 'Save all changes'}
                </button>
              </div>
            </div>
          )}

          <div className="overflow-x-auto overflow-y-visible" ref={scrollRef}>
            {/* Mobile: Show only current month + 2 adjacent months */}
            <div className="block lg:hidden">
              <div className="text-sm text-gray-600 dark:text-gray-400 mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <strong>Mobile View:</strong> Showing current month and adjacent months. Use horizontal scroll or switch to desktop view for full spreadsheet.
              </div>
            </div>
            
            <table className="w-full">
              <thead>
                <tr>
                  <th 
                    className="sticky left-0 z-20 bg-gray-50 dark:bg-gray-900 px-4 py-3 text-left text-sm font-semibold text-gray-900 dark:text-white border-r border-gray-200/50 dark:border-gray-700/50 shadow-sm"
                    aria-label="Campaign name and details"
                  >
                    Campaign
                  </th>
              {monthEntries.map((entry, index) => {
                const { isCurrentMonth, borderClass } = getMonthCellMeta(entry, index);
                return (
                  <th
                    key={`${entry.iso}-${index}`}
                    data-month-col="true"
                    ref={el => { headerRefs.current[index] = el; }}
                    className={`px-6 py-4 text-center text-sm font-semibold min-w-[120px] text-gray-900 dark:text-white ${borderClass} ${isCurrentMonth ? 'bg-blue-50 dark:bg-blue-950/60 text-blue-900 dark:text-blue-200 shadow-inner border-blue-200 dark:border-blue-800/50' : 'bg-gray-50 dark:bg-gray-900/50'} transition-all duration-200 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-400/40 motion-reduce:transition-none`}
                    aria-label={`${entry.label} ${entry.year}`}
                    aria-current={isCurrentMonth ? 'date' : undefined}
                  >
                    <div className="flex flex-col items-center gap-1">
                      <span>{entry.shortLabel}</span>
                      <span className={`text-[10px] uppercase tracking-widest font-medium ${isCurrentMonth ? 'text-blue-600 dark:text-blue-400' : 'text-gray-500 dark:text-gray-400'}`}>
                        {entry.year}
                      </span>
                    </div>
                  </th>
                );
              })}
              <th className="sticky right-0 z-20 px-6 py-4 text-center text-sm font-semibold text-gray-900 dark:text-white min-w-[120px] border-l-2 border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900 shadow-lg transition-all duration-200 ease-out">
                Total
              </th>
            </tr>
          </thead>
          <tbody>
            {releases.map((release) => {
              const releaseTotal = monthEntries.reduce((sum, entry) => {
                const key = buildKey(release.id, entry.iso);
                const data = budgetData.get(key);
                return sum + (data?.invoicedAmount || 0);
              }, 0);

              const adSpendTotal = monthEntries.reduce((sum, entry) => {
                const key = buildKey(release.id, entry.iso);
                const data = budgetData.get(key);
                return sum + (data?.adSpent || 0);
              }, 0);

              const remainingTotal = monthEntries.reduce((sum, entry) => {
                const key = buildKey(release.id, entry.iso);
                const data = budgetData.get(key);
                const remaining = (data?.invoicedAmount || 0) - (data?.adSpent || 0);
                return sum + remaining;
              }, 0);

              return (
                <React.Fragment key={release.id}>
                  {/* Release Header Row */}
                  <tr className="bg-blue-500 bg-opacity-20">
                    <td 
                      className="sticky left-0 z-10 bg-blue-500 bg-opacity-20 px-4 py-2 border-r border-gray-200/50 dark:border-gray-700/50"
                      style={{ minWidth: '300px' }}
                    >
                      <div className="flex items-center gap-3">
                        {release.artwork_url ? (
                          <img src={release.artwork_url} alt={release.title} className="w-10 h-10 rounded flex-shrink-0" />
                        ) : (
                          <Music className="w-10 h-10 text-gray-400 flex-shrink-0" />
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-gray-900 dark:text-white truncate">
                            {release.title}
                          </div>
                          <div className="text-sm text-gray-600 dark:text-gray-400 truncate">
                            {release.artist?.name || 'Unknown Artist'}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            {((release.campaigns?.length || 0) + (release.metaCampaigns?.length || 0))} campaigns
                          </div>
                        </div>
                      </div>
                    </td>
                    <td colSpan={monthEntries.length + 1} className="px-4 py-2">
                      {/* Empty cell for month columns */}
                    </td>
                  </tr>

                  {/* Budget Amount Row */}
                  <tr>
                    <td 
                      className="sticky left-0 z-10 bg-white dark:bg-gray-800 px-6 py-3 text-sm font-medium text-gray-700 dark:text-gray-200 border-r border-gray-200/50 dark:border-gray-700/50 shadow-sm"
                      aria-label="Planned budget amount for this release in this month"
                    >
                      Budgeted Amount
                    </td>
                    {monthEntries.map((entry, monthIndex) => {
                      const key = buildKey(release.id, entry.iso);
                      const data = budgetData.get(key);
                      const isSelected = selectedCell?.releaseId === release.id && selectedCell?.month === entry.iso;
                      const status = getBudgetStatus(release, monthIndex);
                      const { isCurrentMonth, borderClass } = getMonthCellMeta(entry, monthIndex);
                      const highlightRing = isCurrentMonth && !isSelected ? 'ring-1 ring-inset ring-blue-200 dark:ring-blue-800/40' : '';
                      const statusBackground = status === 'critical'
                        ? 'bg-red-50/80 dark:bg-red-950/20 border-red-200/50 dark:border-red-900/30'
                        : status === 'warning'
                          ? 'bg-amber-50/80 dark:bg-amber-950/20 border-amber-200/50 dark:border-amber-900/30'
                          : 'bg-white/80 dark:bg-gray-900/60 backdrop-blur-sm';
                      const cellTitle = data?.lastSyncedAt ? `Last synced ${new Date(data.lastSyncedAt).toLocaleString()}` : undefined;

                      return (
                        <td
                          key={entry.iso}
                          className={`px-4 py-3 text-center text-sm cursor-pointer border border-gray-100/50 dark:border-gray-800/40 ${borderClass} ${statusBackground} ${highlightRing} hover:bg-gray-50/80 dark:hover:bg-gray-800/60 transition-all duration-200 ease-out motion-reduce:transition-none`}
                          onClick={() => canEdit && selectCell(release.id, monthIndex)}
                          aria-label={cellTitle}
                        >
                          {isSelected && canEdit ? (
                            <div className="relative">
                              <input
                                ref={inputRef}
                                type="number"
                                value={editingValue}
                                onChange={(e) => handleBudgetInputChange(e.target.value)}
                                onKeyDown={(e) => handleKeyDown(e, release.id, monthIndex)}
                                onBlur={saveCell}
                                className="w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white/90 dark:bg-gray-800/90 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:border-blue-500 dark:focus:border-blue-400 focus:ring-2 focus:ring-blue-500/20 dark:focus:ring-blue-400/20 focus:outline-none backdrop-blur-sm transition-all duration-200 ease-out"
                                style={{
                                  WebkitAppearance: 'none',
                                  MozAppearance: 'textfield',
                                  appearance: 'none'
                                }}
                                step="0.01"
                                min={MIN_BUDGET_VALUE}
                                max={MAX_BUDGET_VALUE}
                                inputMode="decimal"
                                tabIndex={0}
                                role="textbox"
                                aria-label={`Budget amount for ${release.title} in ${entry.label} ${entry.year}`}
                                aria-invalid={Boolean(inputError)}
                                aria-describedby={inputError ? 'budget-input-error' : undefined}
                              />
                              {inputError && editingCell?.releaseId === release.id && editingCell?.month === entry.iso && (
                                <div className="absolute -bottom-6 left-0 text-xs text-red-600 bg-red-50 px-2 py-1 rounded border">
                                  {inputError}
                                </div>
                              )}
                            </div>
                          ) : (
                            <span className="tabular-nums cursor-pointer hover:bg-gray-100/60 dark:hover:bg-gray-700/60 px-3 py-1.5 rounded-md transition-all duration-150 ease-out font-medium text-gray-900 dark:text-gray-100">
                              ${formatBudgetInput(data?.invoicedAmount ?? 0)}
                            </span>
                          )}
                        </td>
                      );
                    })}
                    <td className="sticky right-0 z-10 px-4 py-3 text-center text-sm font-semibold text-gray-900 dark:text-white border-l-2 border-gray-200 dark:border-gray-800 bg-gray-50/80 dark:bg-gray-900/60 backdrop-blur-sm shadow-lg">
                      ${formatBudgetInput(releaseTotal)}
                    </td>
                  </tr>

                  {/* Ad Spend Row */}
                  <tr>
                    <td 
                      className="sticky left-0 z-10 bg-white dark:bg-gray-800 px-6 py-3 text-sm font-medium text-gray-700 dark:text-gray-200 border-r border-gray-200/50 dark:border-gray-700/50 shadow-sm"
                      aria-label="Actual amount spent on advertising for this release in this month"
                    >
                      Ad Spend
                    </td>
                    {monthEntries.map((entry, monthIndex) => {
                      const key = buildKey(release.id, entry.iso);
                      const data = budgetData.get(key);
                      const { isCurrentMonth, borderClass } = getMonthCellMeta(entry, monthIndex);
                      const highlightClass = isCurrentMonth ? 'bg-blue-50/40 dark:bg-blue-950/30 font-medium text-blue-900 dark:text-blue-100' : 'bg-white dark:bg-gray-800';
                      // Treat all conversion ad releases as Meta Ads-driven for UX clarity
                      const isMetaAdsData = release.metaCampaigns && release.metaCampaigns.length > 0;

                      return (
                        <td
                          key={entry.iso}
                          className={`px-4 py-2 text-center text-sm text-gray-600 dark:text-gray-200 border border-gray-100 dark:border-gray-700/60 ${borderClass} ${highlightClass}`}
                        >
                          <div className="flex items-center justify-center gap-1">
                            <span className="tabular-nums">${formatBudgetInput(data?.adSpent ?? 0)}</span>
                            {isMetaAdsData && (
                              <RefreshCw className="h-3 w-3 text-blue-500 dark:text-blue-400 opacity-60" />
                            )}
                          </div>
                        </td>
                      );
                    })}
                    <td className="sticky right-0 z-10 px-4 py-2 text-center text-sm font-semibold text-gray-900 dark:text-white border-l-2 border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg">
                      ${formatBudgetInput(adSpendTotal)}
                    </td>
                  </tr>

                  {/* Remaining Row */}
                  <tr className="border-b border-gray-200 dark:border-gray-700">
                    <td 
                      className="sticky left-0 z-10 bg-white dark:bg-gray-800 px-6 py-3 text-sm font-medium text-gray-700 dark:text-gray-200 border-r border-gray-200/50 dark:border-gray-700/50 shadow-sm"
                      aria-label="Remaining budget equals Budgeted Amount minus Ad Spend"
                    >
                      Remaining Budget
                    </td>
                    {monthEntries.map((entry, monthIndex) => {
                      const key = buildKey(release.id, entry.iso);
                      const data = budgetData.get(key);
                      const remaining = (data?.invoicedAmount || 0) - (data?.adSpent || 0);
                      const { isCurrentMonth, borderClass } = getMonthCellMeta(entry, monthIndex);
                      const highlightClass = isCurrentMonth ? 'bg-blue-50/30 dark:bg-blue-950/30 font-medium text-blue-900 dark:text-blue-100' : 'bg-white dark:bg-gray-800';

                      return (
                        <td
                          key={entry.iso}
                          className={`px-4 py-2 text-center text-sm border border-gray-100 dark:border-gray-700/60 ${borderClass} ${highlightClass}`}
                        >
                          <span className={`tabular-nums ${remaining < 0 ? 'text-red-600 dark:text-red-400 font-semibold' : remaining === 0 ? 'text-gray-500 dark:text-gray-300' : 'text-gray-900 dark:text-gray-100'}`}>
                            ${formatBudgetInput(remaining)}
                          </span>
                          {remaining < 0 && (
                            <div className="text-xs text-red-500 mt-1">
                              Over budget
                            </div>
                          )}
                        </td>
                      );
                    })}
                    <td className="sticky right-0 z-10 px-4 py-2 text-center text-sm font-semibold text-gray-900 dark:text-white border-l-2 border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg">
                      <span className={`tabular-nums ${remainingTotal < 0 ? 'text-red-600 dark:text-red-400' : ''}`}>
                        ${formatBudgetInput(remainingTotal)}
                      </span>
                    </td>
                  </tr>

                  {/* Performance Intelligence Rows */}
                  {showPerformanceRows && (
                    <>
                      {/* Performance Score Row */}
                      <tr className="bg-green-50 dark:bg-green-900 bg-opacity-30">
                        <td className="sticky left-0 bg-green-50 dark:bg-green-900 bg-opacity-30 px-4 py-2 text-sm text-green-700 dark:text-green-200 font-medium">
                          Performance Score
                        </td>
                        {monthEntries.map((entry, monthIndex) => {
                          const metrics = calculatePerformanceMetrics(release, monthIndex);
                          const { isCurrentMonth, borderClass } = getMonthCellMeta(entry, monthIndex);
                          const highlightRing = isCurrentMonth ? 'ring-1 ring-inset ring-blue-300 dark:ring-blue-600' : '';

                          return (
                            <td
                              key={entry.iso}
                              className={`px-4 py-2 text-center text-sm font-medium border border-green-100 dark:border-green-900/40 ${borderClass} ${highlightRing}`}
                            >
                              <div className="flex items-center justify-center gap-1">
                                <span className={`tabular-nums ${getPerformanceColor(metrics.performanceScore, 'score')}`}>
                                  {metrics.performanceScore > 0 ? `${metrics.performanceScore.toFixed(0)}%` : '—'}
                                </span>
                                {metrics.trend === 'up' && (
                                  <TrendingUp className="h-4 w-4 text-green-500" aria-hidden="true" />
                                )}
                                {metrics.trend === 'down' && (
                                  <TrendingDown className="h-4 w-4 text-red-500" aria-hidden="true" />
                                )}
                                {metrics.trend === 'stable' && (
                                  <Minus className="h-4 w-4 text-gray-400" aria-hidden="true" />
                                )}
                              </div>
                            </td>
                          );
                        })}
                        <td className="sticky right-0 z-10 px-4 py-2 text-center text-sm font-medium border-l-2 border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900 bg-opacity-30 shadow-lg">
                          -
                        </td>
                      </tr>
                    </>
                  )}

                  {/* Forecast Rows */}
                  {showForecastRows && (
                    <>
                      {/* Projected Spend Row */}
                      <tr className="bg-purple-50 dark:bg-purple-900 bg-opacity-30">
                        <td className="sticky left-0 bg-purple-50 dark:bg-purple-900 bg-opacity-30 px-4 py-2 text-sm text-purple-700 dark:text-purple-200 font-medium">
                          Projected Spend
                        </td>
                        {monthEntries.map((entry, monthIndex) => {
                          const key = buildKey(release.id, entry.iso);
                          const data = budgetData.get(key);
                          const metrics = calculatePerformanceMetrics(release, monthIndex);
                          const { isCurrentMonth, borderClass } = getMonthCellMeta(entry, monthIndex);
                          const highlightRing = isCurrentMonth ? 'ring-1 ring-inset ring-blue-300 dark:ring-blue-600' : '';
                          const projectedClass = metrics.projectedSpend > (data?.invoicedAmount || 0)
                            ? 'text-red-600 dark:text-red-400 font-semibold'
                            : 'text-purple-600 dark:text-purple-300 font-medium';

                          return (
                            <td
                              key={entry.iso}
                              className={`px-4 py-2 text-center text-sm border border-purple-100 dark:border-purple-900/40 ${borderClass} ${highlightRing}`}
                            >
                              <span className={`tabular-nums ${projectedClass}`}>
                                ${formatBudgetInput(metrics.projectedSpend)}
                              </span>
                            </td>
                          );
                        })}
                        <td className="sticky right-0 z-10 px-4 py-2 text-center text-sm font-medium border-l-2 border-purple-200 dark:border-purple-800 bg-purple-50 dark:bg-purple-900 bg-opacity-30 shadow-lg">
                          -
                        </td>
                      </tr>

                      {/* Suggested Action Row */}
                      <tr className="bg-amber-50 dark:bg-amber-900 bg-opacity-30">
                        <td className="sticky left-0 bg-amber-50 dark:bg-amber-900 bg-opacity-30 px-4 py-2 text-sm text-amber-700 dark:text-amber-200 font-medium">
                          Suggested Action
                        </td>
                        {monthEntries.map((entry, monthIndex) => {
                          const key = buildKey(release.id, entry.iso);
                          const data = budgetData.get(key);
                          const metrics = calculatePerformanceMetrics(release, monthIndex);

                          const { icon, tone, label } = SUGGESTED_ACTION_ICONS[metrics.suggestedAction];
                          const ActionIcon = icon;
                          const shortLabel = label.split(' ')[0];
                          const { isCurrentMonth, borderClass } = getMonthCellMeta(entry, monthIndex);
                          const highlightRing = isCurrentMonth ? 'ring-1 ring-inset ring-blue-300 dark:ring-blue-600' : '';

                          return (
                            <td
                              key={entry.iso}
                              className={`px-4 py-2 text-center text-sm border border-amber-100 dark:border-amber-900/40 ${borderClass} ${highlightRing}`}
                            >
                              {data?.adSpent ? (
                                <span className={`inline-flex items-center justify-center gap-1 text-sm font-medium ${tone}`} title={label}>
                                  <ActionIcon className="h-4 w-4" aria-hidden="true" />
                                  <span className="hidden xl:inline">{shortLabel}</span>
                                  <span className="sr-only">{label}</span>
                                </span>
                              ) : (
                                <span className="text-gray-400 dark:text-gray-500">—</span>
                              )}
                            </td>
                          );
                        })}
                        <td className="sticky right-0 z-10 px-4 py-2 text-center text-sm font-medium border-l-2 border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900 bg-opacity-30 shadow-lg">
                          -
                        </td>
                      </tr>
                    </>
                  )}

                  {/* Total Row */}
                  <tr className="bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700">
                    <td className="sticky left-0 z-10 bg-gray-50 dark:bg-gray-900 px-4 py-3 text-sm font-bold text-gray-900 dark:text-white border-r border-gray-200/60 dark:border-gray-700/60 shadow-sm">
                      Budget Total
                    </td>
                    {monthEntries.map((entry, monthIndex) => {
                      const key = buildKey(release.id, entry.iso);
                      const data = budgetData.get(key);
                      const status = getBudgetStatus(release, monthIndex);
                      const statusColors = {
                        normal: 'text-green-600 dark:text-green-400',
                        warning: 'text-yellow-600 dark:text-yellow-400',
                        critical: 'text-red-600 dark:text-red-400'
                      };
                      const { isCurrentMonth, borderClass } = getMonthCellMeta(entry, monthIndex);
                      const highlightRing = isCurrentMonth ? 'ring-1 ring-inset ring-blue-300 dark:ring-blue-600' : '';

                      return (
                        <td
                          key={entry.iso}
                          className={`px-4 py-3 text-center text-sm font-bold border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900 ${borderClass} ${highlightRing}`}
                        >
                          <span className={`tabular-nums ${statusColors[status]}`}>
                            ${formatBudgetInput(data?.invoicedAmount ?? 0)}
                          </span>
                        </td>
                      );
                    })}
                    <td className="sticky right-0 z-10 px-4 py-3 text-center text-sm font-bold text-gray-900 dark:text-white border-l-2 border-gray-300 dark:border-gray-800 bg-gray-50 dark:bg-gray-900 shadow-lg">
                      ${formatBudgetInput(releaseTotal)}
                    </td>
                  </tr>

                  {/* Spacer Row */}
                  <tr>
                    <td colSpan={monthEntries.length + 2} className="h-4 border-b-4 border-gray-100 dark:border-gray-700"></td>
                  </tr>
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {releases.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-500 dark:text-gray-400">No releases found.</p>
          <Link to="/releases" className="text-blue-500 hover:underline mt-2 inline-block">
            Create a release
          </Link>
        </div>
      )}
      {isExportModalOpen && (
        <div className="fixed inset-0 z-[70]" role="dialog" aria-modal="true">
          <div className="absolute inset-0 bg-black/50" onClick={() => setIsExportModalOpen(false)} />
          <div
            className="absolute inset-x-0 top-12 mx-auto w-full max-w-2xl rounded-lg border border-gray-200 bg-white shadow-xl dark:border-gray-700 dark:bg-gray-900"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-gray-200 p-4 dark:border-gray-700">
              <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Export Budgets</h2>
              <button
                onClick={() => setIsExportModalOpen(false)}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                aria-label="Close export modal"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="grid grid-cols-1 gap-6 p-6 md:grid-cols-5">
              <div className="space-y-4 md:col-span-2">
                <div>
                  <label htmlFor="export-start-month" className="block text-xs font-medium text-gray-600 dark:text-gray-300">
                    From
                  </label>
                  <input
                    id="export-start-month"
                    type="month"
                    className="mt-1 w-full rounded-md border border-gray-300 px-2 py-1 text-sm focus:border-blue-500 focus:outline-none dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                    value={exportStartMonth}
                    min={monthEntries[0]?.iso}
                    max={monthEntries[monthEntries.length - 1]?.iso}
                    onChange={(e) => setExportStartMonth(e.target.value)}
                  />
                </div>
                <div>
                  <label htmlFor="export-end-month" className="block text-xs font-medium text-gray-600 dark:text-gray-300">
                    To
                  </label>
                  <input
                    id="export-end-month"
                    type="month"
                    className="mt-1 w-full rounded-md border border-gray-300 px-2 py-1 text-sm focus:border-blue-500 focus:outline-none dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                    value={exportEndMonth}
                    min={monthEntries[0]?.iso}
                    max={monthEntries[monthEntries.length - 1]?.iso}
                    onChange={(e) => setExportEndMonth(e.target.value)}
                  />
                </div>
              </div>
              <div className="md:col-span-3">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-medium text-gray-600 dark:text-gray-300">
                    Campaigns
                  </label>
                  <div className="flex items-center gap-2 text-xs text-blue-600 dark:text-blue-300">
                    <button
                      type="button"
                      onClick={() => setExportCampaignIds(releases.map(r => r.id))}
                      className="hover:underline"
                    >
                      Select All
                    </button>
                    <span>|</span>
                    <button
                      type="button"
                      onClick={() => setExportCampaignIds([])}
                      className="hover:underline"
                    >
                      Clear
                    </button>
                  </div>
                </div>
                <div className="mt-3 max-h-64 space-y-2 overflow-y-auto pr-1">
                  {releases.length === 0 ? (
                    <p className="text-xs text-gray-500 dark:text-gray-400">No releases available.</p>
                  ) : (
                    releases.map(release => {
                      const checked = exportCampaignIds.includes(release.id);
                      return (
                        <label
                          key={release.id}
                          className="flex items-start gap-2 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-xs shadow-sm hover:border-blue-300 dark:border-gray-700 dark:bg-gray-900/50 dark:hover:border-blue-500"
                        >
                          <input
                            type="checkbox"
                            className="mt-0.5"
                            checked={checked}
                            onChange={(e) => {
                              setExportCampaignIds(prev => {
                                if (e.target.checked) {
                                  return prev.includes(release.id) ? prev : [...prev, release.id];
                                }
                                return prev.filter(id => id !== release.id);
                              });
                            }}
                          />
                          <span className="flex-1">
                            <span className="block font-medium text-gray-900 dark:text-gray-100">{release.title}</span>
                            <span className="block text-[11px] text-gray-500 dark:text-gray-400">
                              {release.artist?.name ?? 'Unknown Artist'} • {`${release.campaigns?.length || 0} campaigns`}
                              {false && ' (Archived)'}
                            </span>
                          </span>
                        </label>
                      );
                    })
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 border-t border-gray-200 p-4 dark:border-gray-700">
              <button
                onClick={() => setIsExportModalOpen(false)}
                className="px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  const didExport = exportToCSV(exportStartMonth, exportEndMonth, exportCampaignIds);
                  if (didExport) {
                    setIsExportModalOpen(false);
                  }
                }}
                disabled={!exportStartMonth || !exportEndMonth || exportCampaignIds.length === 0}
                className={PRIMARY_BUTTON_CLASSES}
              >
                <Download className="h-4 w-4" />
                Export CSV
              </button>
            </div>
          </div>
        </div>
      )}
        </div>
      </div>

      {/* Add Release Modal */}
      <AddReleaseModal
        isOpen={showAddReleaseModal}
        onClose={() => setShowAddReleaseModal(false)}
        onAddReleases={async (releaseIds) => {
          const existingIds = JSON.parse(localStorage.getItem('budgetManagerReleases') || '[]');
          const newIds = Array.from(new Set([...existingIds, ...releaseIds]));
          localStorage.setItem('budgetManagerReleases', JSON.stringify(newIds));
          
          setShowAddReleaseModal(false);
          await loadData();
          toast.success(`Added ${releaseIds.length} release(s) to budget view`);
        }}
        existingReleaseIds={releases.map(r => r.id)}
      />
    </div>
  );
};
